import { useRef } from 'react'

import { motion, useInView } from 'framer-motion'

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 16 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1, ease: [0.25, 0, 0.1, 1], delay }}
    >
      {children}
    </motion.div>
  )
}

export default function CancellationPolicy() {
  return (
    <div className="min-h-screen bg-cream-100">

      {/* Hero */}
      <div className="relative h-72 lg:h-96 overflow-hidden">
        <img
          src="/images/optimized/IMG_5118.webp"
          fetchPriority="high"
          className="h-full w-full object-cover"
          alt="Mountain view at Scenic Solitude"
        />
        <div className="absolute inset-0 bg-forest-950/55" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <h1 className="text-4xl lg:text-5xl font-semibold text-white tracking-tight">Changes and Cancellations</h1>
          <p className="mt-3 text-sm text-white/60 max-w-md">Please read before finalizing your journey with us.</p>
        </div>
      </div>

      {/* Body */}
      <div className="mx-auto max-w-2xl px-8 lg:px-0 py-20 space-y-14">

        <FadeIn>
          <p className="text-[10px] tracking-[0.35em] uppercase text-ink-light font-semibold mb-4">Cancellation Policy.</p>
          <p className="text-sm text-ink-soft leading-[1.95]">
            Scenic Solitude is a very small sanctuary with only a few rooms and treehouses. We hold each reservation exclusively for you. When a booking is changed or cancelled at the last minute, it is very difficult for us to welcome other travelers up to the mountain. We ask that you review the timelines below before confirming your stay.
          </p>
        </FadeIn>

        {/* 01 */}
        <FadeIn delay={0.05}>
          <div className="flex items-baseline gap-4 mb-5">
            <span className="text-[10px] tracking-[0.3em] uppercase text-ink-light font-semibold">01</span>
            <h2 className="text-xl font-semibold text-ink tracking-tight">Cancellation Timelines</h2>
          </div>
          <div className="text-sm text-ink-soft leading-[1.95] space-y-4">
            <p>
              <strong className="text-ink font-medium">30 days or more before arrival:</strong> If your plans change early, we will provide a full refund of your deposit, minus a small bank processing fee.
            </p>
            <p>
              <strong className="text-ink font-medium">15 to 30 days before arrival:</strong> You will receive a 50% refund, or you can choose to move your stay to any available dates within the next six months.
            </p>
            <p>
              <strong className="text-ink font-medium">Less than 15 days before arrival:</strong> We are unable to offer refunds or changes to your dates. At this stage, our team has already prepared your room and sourced fresh ingredients from the valley for your stay.
            </p>
          </div>
        </FadeIn>

        {/* 02 */}
        <FadeIn delay={0.08}>
          <div className="flex items-baseline gap-4 mb-5">
            <span className="text-[10px] tracking-[0.3em] uppercase text-ink-light font-semibold">02</span>
            <h2 className="text-xl font-semibold text-ink tracking-tight">Mountain Weather and Road Closures</h2>
          </div>
          <div className="text-sm text-ink-soft leading-[1.95] space-y-4">
            <p>
              Nature dictates life at 2,700 meters. We understand that the mountains can be unpredictable, and we try to be fair.
            </p>
            <p>
              <strong className="text-ink font-medium">Severe disruptions:</strong> If an official government advisory, landslide, or severe weather event completely closes the main access roads to Sojha on your arrival date, we will not let you lose your investment. We will issue a full credit note to rebook your stay anytime within the next year.
            </p>
            <p>
              <strong className="text-ink font-medium">General weather:</strong> We do not offer refunds or date changes for general mountain weather such as rain, thick mist, unexpected snow, or seasonal cold. We believe these natural shifts are a beautiful part of the true Himalayan experience.
            </p>
          </div>
        </FadeIn>

        {/* 03 */}
        <FadeIn delay={0.1}>
          <div className="flex items-baseline gap-4 mb-5">
            <span className="text-[10px] tracking-[0.3em] uppercase text-ink-light font-semibold">03</span>
            <h2 className="text-xl font-semibold text-ink tracking-tight">Early Departures</h2>
          </div>
          <div className="text-sm text-ink-soft leading-[1.95]">
            <p>
              If you decide to cut your journey short and leave the property before your scheduled check-out date, we cannot refund the remaining nights. We encourage you to reach out to us directly if something unexpected arises and we will always do our best to find a fair way forward.
            </p>
          </div>
        </FadeIn>

        {/* Closing */}
        <FadeIn delay={0.12} className="border-t border-cream-300 pt-12">
          <p className="text-sm text-ink-soft leading-[1.95] italic">
            If you have any questions about your booking or these terms, please write to us at bookings@scenicsolitudehimalayas.com. We are a small team and we will always respond with care.
          </p>
        </FadeIn>

      </div>
    </div>
  )
}
